export const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

/** Normalized 0..1 progress of `t` across the [from, to] window. */
export const ramp = (t: number, from: number, to: number): number =>
  clamp((t - from) / (to - from), 0, 1);

/** Newton-Raphson solve of a CSS cubic-bezier timing curve. */
export const cubicBezier = (
  x1: number,
  y1: number,
  x2: number,
  y2: number
): ((x: number) => number) => {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;

  const sampleX = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const sampleSlope = (t: number): number => (3 * ax * t + 2 * bx) * t + cx;

  return (x: number): number => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;

    let t = x;
    for (let i = 0; i < 6; i++) {
      const error = sampleX(t) - x;
      if (Math.abs(error) < 1e-5) break;
      const slope = sampleSlope(t);
      if (Math.abs(slope) < 1e-6) break;
      t -= error / slope;
    }
    return ((ay * t + by) * t + cy) * t;
  };
};

export const easeEnter = cubicBezier(0.22, 0.9, 0.24, 1);
export const easePop = cubicBezier(0.4, 0, 0.2, 1);
export const easeDraw = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;

/* Accepts both #rrggbb and the rgb() strings mixHex itself emits, so mixes
   can be composed without NaN channels silently dropping the style. */
const channels = (color: string): [number, number, number] => {
  if (color[0] === '#') {
    return [
      parseInt(color.slice(1, 3), 16),
      parseInt(color.slice(3, 5), 16),
      parseInt(color.slice(5, 7), 16),
    ];
  }
  const match = color.match(/(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (!match) return [0, 0, 0];
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};

/** Linear RGB interpolation between two six-digit hex colors. */
export const mixHex = (from: string, to: string, t: number): string => {
  const a = channels(from);
  const b = channels(to);
  const blended = a.map((value, i) => Math.round(value + (b[i] - value) * t));
  return `rgb(${blended.join(',')})`;
};
